// libs
import React, { useCallback, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
// components
import { Button } from '@lyearn/ui';
import * as ContentListModal from '@/components/ContentListModal';
import Loader from '@/components/Loader';
import useEntitySet from '@/hooks/useEntitySet';
import useParams from '@/hooks/useParams';
// hooks
import useScorecards from '@/modules/corporate/modules/exercise/views/Creator/hooks/useScorecards';
import ScorecardPreviewModal from '@/modules/corporate/modules/scorecards/components/ScorecardPreviewModal';
import ScorecardViewer from '@/modules/corporate/modules/scorecards/components/ScorecardViewer';
// types
import { OrderEnum } from '@/types/schema';

import { ScorecardFragment } from '../../graphql/fragments/generated/Scorecard';
import EmptyState from './components/EmptyState';
// style
import { createScorecardBtnStyles } from './style';

const SCORECARD_LIST_SORT_BY = { field: 'createdAt', order: OrderEnum.Desc };

function SelectScorecardsDialog({
  onAttach,
  onClose,
  selectedScorecardIds,
  onCreateScorecard,
}: {
  onAttach: (scorecardIds?: string[]) => void;
  onClose: () => void;
  selectedScorecardIds?: string[];
  onCreateScorecard: () => void;
}) {
  const { t } = useTranslation();
  const [searchString, setSearchString] = useState<string>('');
  const { departmentId } = useParams<{ departmentId: string }>();
  const { scorecards, initialFetching, fetchMore } = useScorecards({
    searchString: searchString,
    sort: SCORECARD_LIST_SORT_BY,
  });
  const [previewScorecard, setPreviewScorecard] = useState<ScorecardFragment | null>(null);
  const closePreviewModal = useCallback(() => setPreviewScorecard(null), [setPreviewScorecard]);
  const { selectedItemsSet, toggle } = useEntitySet(selectedScorecardIds);
  const attatchScorecards = useCallback(() => {
    onAttach(Array.from(selectedItemsSet));
    onClose();
  }, [onClose, onAttach, selectedItemsSet]);

  return (
    <>
      <ContentListModal.Main
        action={{
          discardText: t('Common:Cancel'),
          onSubmit: attatchScorecards,
          text: t('Scorecard:AttachScorecard'),
        }}
        fetchMore={fetchMore}
        header={{
          title: t('Scorecard:SelectScorecards'),
          description: t('Scorecard:SelectScorecardsDescription'),
          children: (
            <Button
              alignment="left"
              className={cn(
                'mb-12 -mt-14 h-auto pt-0 pb-8 text-link-01 pe-0 ps-8',
                createScorecardBtnStyles,
              )}
              variant="ghost"
              onClick={onCreateScorecard}>
              {t('Scorecard:OrCreateScorecard')}
            </Button>
          ),
        }}
        items={scorecards}
        searchBar={{
          value: searchString,
          onChange: setSearchString,
          selectedCount: selectedItemsSet.size,
        }}
        onClose={onClose}>
        {scorecards?.length ? (
          <ContentListModal.Body
            className="flex-1"
            itemRenderer={(index: number) => {
              const currentScorecard = scorecards?.[index];

              if (!currentScorecard) {
                return null;
              }

              return (
                <ScorecardViewer
                  key={currentScorecard.id}
                  className="mb-12"
                  isSelected={selectedItemsSet.has(currentScorecard.id)}
                  scorecard={currentScorecard}
                  onPreview={setPreviewScorecard}
                  onToggle={toggle}
                />
              );
            }}
          />
        ) : initialFetching ? (
          <Loader />
        ) : (
          <EmptyState departmentId={departmentId} searchString={searchString} />
        )}
      </ContentListModal.Main>
      {previewScorecard ? (
        <ScorecardPreviewModal showBack scorecard={previewScorecard} onClose={closePreviewModal} />
      ) : null}
    </>
  );
}

export default SelectScorecardsDialog;
